import {
	type Currency,
	currencySchema,
	FileType,
	type ISODate,
	isoDateSchema,
	wipStatementFileSchema,
} from "@inrev/common";
import { FormSection } from "@portal/components/form";
import { FormCurrencyLineItem, FormDateComponent } from "@portal/components/form/fieldLayouts";
import { Modal } from "@portal/components/layout/Modal";
import { ModalItemWithHeader } from "@portal/components/layout/ModalItemWithHeader";
import { Button, Card } from "@portal/components/primitives";
import { FileUpload } from "@portal/components/ui/Upload";
import { trackingAttrs } from "@portal/components/utils";
import { DateTime } from "luxon";
import { useController, useForm } from "react-hook-form";
import { z } from "zod";
import { typedZodResolver } from "../../../../utils/form";

const formSchema = z.object({
	date: isoDateSchema,
	projectBacklogCost: currencySchema.optional(),
	projectBacklogGrossProfit: currencySchema.optional(),
	files: z.array(wipStatementFileSchema),
});

type WIPFormInput = {
	date: string;
	projectBacklogCost?: string;
	projectBacklogGrossProfit?: string;
	files: z.infer<typeof wipStatementFileSchema>[];
};

export type WIPFormOutput = {
	date: ISODate;
	projectBacklogCost?: Currency;
	projectBacklogGrossProfit?: Currency;
	files: z.infer<typeof wipStatementFileSchema>[];
};

type WIPStatementModalProps = {
	mode: "create" | "edit";
	defaultValues?: Partial<WIPFormInput>;
	disableDatePickerDays?: string[];
	readOnlyDate?: boolean;
	onSubmit: (data: WIPFormOutput) => void;
	onClose: () => void;
};

export const WIPStatementModal = ({
	mode,
	defaultValues,
	disableDatePickerDays,
	readOnlyDate,
	onSubmit,
	onClose,
}: WIPStatementModalProps) => {
	const formMethods = useForm<WIPFormInput, unknown, WIPFormOutput>({
		mode: "onSubmit",
		reValidateMode: "onBlur",
		defaultValues: {
			date: "",
			projectBacklogCost: undefined,
			projectBacklogGrossProfit: undefined,
			files: [],
			...defaultValues,
		},
		resolver: typedZodResolver(formSchema),
	});

	const filesController = useController({
		control: formMethods.control,
		name: "files",
	});

	return (
		<Modal onClickOutside={onClose}>
			<ModalItemWithHeader
				header={
					mode === "edit" ? "Edit Work In Progress Statement" : "Add Work In Progress Statement"
				}
				onClose={onClose}
				className="h-fit overflow-hidden"
			>
				<div className="h-full w-fit min-w-[747px] flex flex-col">
					<div className="flex-1 overflow-y-auto flex justify-center">
						<div className="w-full max-w-[900px] px-[52px] pt-[18px] pb-5 flex flex-col space-y-[40px]">
							<Card id="wip" elevation="raised" className="w-full h-fit">
								<Card.Header>
									<Card.Title className="text-[15.5px] font-[550]">
										Work In Progress Statement
									</Card.Title>
									<div
										id="pendo-wip-statement"
										{...trackingAttrs("wip-statement-help")}
										className="text-[12px] text-link hover:text-link-hover font-medium underline cursor-pointer"
									>
										Where do I find this?
									</div>
								</Card.Header>
								<Card.Body surface="gray" className="py-[35px] px-[45px] space-y-[18px]">
									<div className="pb-[12px]">
										<FormDateComponent
											formMethods={formMethods}
											name="date"
											label="Statement Date"
											compact
											defaultMonth={DateTime.now()}
											readonly={readOnlyDate}
											disableAfter={DateTime.now().toISODate()}
											disableDays={disableDatePickerDays}
										/>
									</div>
									<FormCurrencyLineItem
										label="Backlog Cost-to-Complete"
										name="projectBacklogCost"
										formMethods={formMethods}
										tooltip="<table width='500'><tr><td>This is the balance of cost left to complete on all jobs in progress. It is usually listed on the Work in Progress statement as &quot;Cost to Complete&quot; or &quot;CTC&quot;</td></tr></table>"
									/>
									<FormCurrencyLineItem
										label="Gross Profit in the Backlog"
										name="projectBacklogGrossProfit"
										formMethods={formMethods}
										tooltip="<table width='500'><tr><td>This is the total gross profit to be earned on all uncompleted work (total estimated gross profit for all projects - gross profit earned to date for all projects). It can be found on the Work in Progress statement and is usually listed as &quot;Gross Profit Remaining&quot;</td></tr></table>"
									/>
								</Card.Body>
							</Card>
							<FormSection
								topPadding
								header="Documents"
								headerClassName="mt-[12px]"
								partial={false}
								subHeader="Please upload a copy of the work in progress schedule, if available"
								id="wip-files"
							>
								<FileUpload
									value={filesController.field.value}
									onChange={filesController.field.onChange}
									onBlur={filesController.field.onBlur}
									showTabs={false}
									allowDelete={true}
									expandedByDefault={true}
									allowedTypesAndLabels={{
										[FileType.wip]: "Work In Progress Schedule",
									}}
									maxTypesPerFile={2}
									analyticsContext="wip-statements"
									trackingPrefix="account-wip-statement-modal-upload"
								/>
							</FormSection>
						</div>
					</div>
					<div className="flex justify-end px-[52px] py-[16px] border-t border-bc-default">
						<Button
							variant="solid"
							color="primary"
							disabled={
								(mode === "create" && !formMethods.formState.isDirty) ||
								formMethods.formState.isSubmitting
							}
							onClick={formMethods.handleSubmit(onSubmit)}
						>
							Save
						</Button>
					</div>
				</div>
			</ModalItemWithHeader>
		</Modal>
	);
};
